import { useCallback, useState } from 'react';

import { ClientDirectorBookingModal } from '~/modules-rocco/booking';
import { useFeature } from '~/modules/root';
import type { ProjectPageLoader } from '@modules/project/type';

type ClientDirectorCalendarSession =
    ProjectPageLoader['project']['ClientDirectorCalendarSession'];

/**
 * Shared "Book a Meeting" wiring for the project detail page. A project links a
 * Client Director meeting session via `ClientDirectorCalendarSession`; when that
 * session is active and one-on-one the CTA is shown.
 *
 * The `book_meeting_open_access` feature flag gates the CTA's visibility:
 * call-sites render the button only when `bookAMeetingEnabled` and
 * `canBookDirect` are both true. When the flag is off the button is hidden and
 * the booking modal is never mounted.
 *
 * Takes the linked session as an argument (rather than reading the loader) so
 * the desktop and mobile CTA call-sites — which already hold the project — can
 * share a single source of truth and stay renderable outside a data router.
 * Each renders its own native button and drops `bookingModal` into its tree.
 */
export const useProjectBookMeeting = (
    session: ClientDirectorCalendarSession,
) => {
    const bookAMeetingEnabled = useFeature('book_meeting_open_access');

    const canBookDirect = Boolean(
        session?.IsActive && session.MeetingType === 'one-on-one',
    );

    const [open, setOpen] = useState(false);
    const openBookingModal = useCallback(() => setOpen(true), []);

    const bookingModal =
        bookAMeetingEnabled && canBookDirect && session ? (
            <ClientDirectorBookingModal
                sessionId={session.ID}
                open={open}
                onOpenChange={setOpen}
            />
        ) : null;

    return {
        canBookDirect,
        bookAMeetingEnabled,
        openBookingModal,
        bookingModal,
    };
};
